import { Box, Grid, SxProps, Typography } from "@mui/material";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { useMyScoringSystemQuery } from "apps/web/src/types";
import { Dispatch, type ReactNode, useState } from "react";
import { Loading } from "shared-ui/global/Loading";
import { FoodFormUiProvider, type FoodFormUiVariant } from "../../FoodFormUi/FoodFormUiContext";
import { LegacyOrderDetails } from "../OrderDetails/LegacyOrderDetails";
import { RedesignOrderDetails } from "../OrderDetails/RedesignOrderDetails";
import { MenuItemAction, MenuItemState } from "../reducer";
import { AddOns } from "./AddOns";
import { Image } from "./Image";
import { Ingredients } from "./Ingredients";
import { ItemDetails } from "./ItemDetails";
import { RedesignImage } from "./RedesignImage";
import { TotalMacros } from "./TotalMacros";

interface MenuItemFullEditFormProps {
  variant: "MenuItem" | "MenuItemAppearance";
  sx?: SxProps;
  state: MenuItemState;
  dispatch: Dispatch<MenuItemAction>;
  onAiGenerateClicked?: null | (() => void);
  onAiTranscribeClicked?: null | (() => void);
  allowSaveAsRecipe?: boolean;
  /** Kitchen create/edit page only — nested dialogs stay legacy. */
  enableRedesign?: boolean;
}

export const MenuItemFullEditForm = ({
  variant,
  sx,
  state,
  dispatch,
  onAiGenerateClicked,
  onAiTranscribeClicked,
  allowSaveAsRecipe,
  enableRedesign = false,
}: MenuItemFullEditFormProps) => {
  const { maxAmount, availableForOrder, allowSpecialRequests } = state;
  const { name, choices, servingAmounts, imageUrl } = state.menuItem;
  const { data: scoringSystemData, loading: loadingScoringSystem } = useMyScoringSystemQuery();
  const scoringSystem = scoringSystemData?.myScoringSystem;
  const { isRecipeAndMenuItemRedesignEnabled } = useNutritionFlags();
  const foodFormUiVariant: FoodFormUiVariant =
    enableRedesign && isRecipeAndMenuItemRedesignEnabled ? "redesign" : "legacy";

  const hasIngredients = state.menuItem.servingAmounts.length > 0;
  const [displayMacros, setDisplayMacros] = useState(true);

  const orderDetailsProps = {
    maxAmount,
    availableForOrder,
    allowSpecialRequests,
    variant,
    onChangeMaxAmount: (ma: number | null) =>
      dispatch({
        type: "MenuItemChangeMaxAmountAction",
        payload: {
          maxAmount: ma,
        },
      }),
    onChangeAvailableForOrder: (ao: boolean) =>
      dispatch({
        type: "MenuItemChangeAvailableForOrderAction",
        payload: {
          availableForOrder: ao,
        },
      }),
    onChangeAllowSpecialRequests: (sr: boolean) =>
      dispatch({
        type: "MenuItemChangeAllowSpecialRequestsAction",
        payload: {
          allowSpecialRequests: sr,
        },
      }),
  };

  const imageProps = {
    imageUrl,
    onChangeImage: (payload: { imageUrl: string | null }) =>
      dispatch({
        type: "MenuItemChangeImageAction",
        payload,
      }),
  };

  const itemDetails = loadingScoringSystem ? <Loading /> : <ItemDetails state={state} dispatch={dispatch} scoringSystem={scoringSystem} />;

  const ingredients = (
    <Ingredients
      itemName={name}
      servingAmounts={servingAmounts}
      onChangeServingAmounts={payload =>
        dispatch({
          type: "MenuItemChangeServingAmountsAction",
          payload,
        })
      }
      onChangeScore={payload =>
        dispatch({
          type: "MenuItemChangeScoreAction",
          payload,
        })
      }
      onAiGenerateClicked={onAiGenerateClicked}
      onAiTranscribeClicked={onAiTranscribeClicked}
      hasIngredients={hasIngredients}
      allowSaveAsRecipe={allowSaveAsRecipe}
      displayMacros={displayMacros}
      onChangeDisplayMacros={setDisplayMacros}
    />
  );

  const addOns = <AddOns choices={choices} onChangeChoices={payload => dispatch({ type: "MenuItemChangeChoicesAction", payload })} />;

  let content: ReactNode;

  if (foodFormUiVariant === "redesign") {
    const cardSx = {
      bgcolor: "common.white",
      border: 1,
      borderColor: "greyscale.200",
      borderRadius: 1,
      p: 3,
    };

    content = (
      <Box sx={[{ bgcolor: "greyscale.50", p: 3 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(320px, 1fr)" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <Box sx={cardSx}>{itemDetails}</Box>
            <Box sx={cardSx}>{ingredients}</Box>
            <Box sx={cardSx}>{addOns}</Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <Box sx={cardSx}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Image
              </Typography>
              <RedesignImage {...imageProps} />
            </Box>
            <Box sx={cardSx}>
              <RedesignOrderDetails {...orderDetailsProps} />
            </Box>
            {displayMacros && (
              <Box sx={cardSx}>
                <TotalMacros servingAmounts={servingAmounts} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  } else {
    content = (
      <Grid container spacing={4} direction="row" sx={sx}>
        <Grid item xs={8}>
          <Grid container direction={"row"} spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  {itemDetails}
                </Grid>
                <Grid item xs={6}>
                  <LegacyOrderDetails {...orderDetailsProps} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  {ingredients}
                </Grid>
                <Grid item xs={6}>
                  {addOns}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={4}>
          <Image {...imageProps} />
        </Grid>
      </Grid>
    );
  }

  return <FoodFormUiProvider variant={foodFormUiVariant}>{content}</FoodFormUiProvider>;
};
